/** Inspection operations. */

import { api, paths } from './client'
import type {
  AdminInspection,
  AdminInspectionDetail,
  InspectionListResponse,
} from '@/types/api'

export interface InspectionFilters {
  status?: string[]
  project_id?: string
  tower_id?: string
  inspector_id?: string
  unit_id?: string
  source?: string
  from?: string
  to?: string
  q?: string
  sort?: 'last_activity' | 'created' | 'submitted'
  cursor?: string
  limit?: number
}

function toQuery(filters: InspectionFilters): Record<string, string | number | undefined> {
  return {
    // The backend takes comma-separated values for multi-select filters.
    status: filters.status?.length ? filters.status.join(',') : undefined,
    project_id: filters.project_id,
    tower_id: filters.tower_id,
    inspector_id: filters.inspector_id,
    unit_id: filters.unit_id,
    source: filters.source,
    from: filters.from,
    to: filters.to,
    q: filters.q,
    sort: filters.sort,
    cursor: filters.cursor,
    limit: filters.limit,
  }
}

export const inspectionsApi = {
  list: (filters: InspectionFilters, signal?: AbortSignal) =>
    api.get<InspectionListResponse>(paths.admin('/inspections'), {
      query: toQuery(filters),
      ...(signal ? { signal } : {}),
    }),

  detail: (id: string, signal?: AbortSignal) =>
    api.get<AdminInspectionDetail>(paths.admin(`/inspections/${id}`), signal ? { signal } : {}),

  reassign: (id: string, inspectorId: string, reason: string) =>
    api.post<AdminInspection>(paths.admin(`/inspections/${id}:reassign`), {
      inspector_id: inspectorId,
      reason,
    }),

  cancel: (id: string, reason: string) =>
    api.post<AdminInspection>(paths.admin(`/inspections/${id}:cancel`), { reason }),

  reopen: (id: string, reason: string) =>
    api.post<AdminInspection>(paths.admin(`/inspections/${id}:reopen`), { reason }),
}

export const inspectionKeys = {
  all: ['inspections'] as const,
  lists: () => [...inspectionKeys.all, 'list'] as const,
  list: (filters: InspectionFilters) => [...inspectionKeys.lists(), filters] as const,
  details: () => [...inspectionKeys.all, 'detail'] as const,
  detail: (id: string) => [...inspectionKeys.details(), id] as const,
}
